// src/operator/console/tenant-api.ts
//
// Helpers HTTP et types partagés pour la gestion multi-tenant (clients,
// sites, grants RBAC, partages inter-tenants). Extraits de clients-console.tsx
// pour être réutilisés par le workspace de paramétrage client/site du cockpit.

// ── Types métier (hiérarchie tenant > client > site > host) ───────────────

export interface Tenant {
  id: string;
  name: string;
  slug: string;
}

export interface Client {
  id: string;
  ownerTenantId: string;
  name: string;
  slug: string;
  createdAt: string;
  policy?: Record<string, unknown> | null;
}

export interface Site {
  id: string;
  clientId: string;
  name: string;
  createdAt: string;
  policy?: Record<string, unknown> | null;
}

export interface Share {
  clientId: string;
  tenantId: string;
  accessLevel: string;
  grantedAt: string;
}

export interface Grant {
  id: string;
  userId: string;
  scopeType: string;
  scopeId: string;
  role: string;
  expiresAt: string | null;
  grantedAt: string;
}

export interface Operator {
  id: string;
  email: string;
  name: string;
  roles: string[];
  tenantId: string | null;
}

// ── Constantes RBAC ──────────────────────────────────────────────────────

export const SCOPE_TYPES = ["tenant", "client", "site", "host"] as const;
export const RBAC_ROLES = ["viewer", "operator", "manager"] as const;
export const ACCESS_LEVELS = ["read_only", "operate"] as const;

// ── Helpers HTTP (credentials: include) ──────────────────────────────────

export async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

export async function jsend(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error((data?.error as string) || `HTTP ${r.status}`);
  return data;
}

// ── Accès API clients / sites ────────────────────────────────────────────

export async function fetchClients(): Promise<Client[]> {
  const d = await jget<{ clients: Client[] }>("/api/clients");
  return d.clients || [];
}

export async function fetchSitesForClient(clientId: string): Promise<Site[]> {
  const d = await jget<{ sites: Site[] }>(`/api/clients/${encodeURIComponent(clientId)}/sites`);
  return d.sites || [];
}

export async function fetchAllSites(): Promise<Site[]> {
  const d = await jget<{ sites: Site[] }>("/api/sites");
  return d.sites || [];
}

export async function fetchTenants(): Promise<Tenant[]> {
  const d = await jget<{ tenants: Tenant[] }>("/tenants");
  return d.tenants || [];
}

export async function patchClient(
  clientId: string,
  patch: Partial<Pick<Client, "name" | "slug" | "policy">>,
): Promise<Client> {
  const d = await jsend(`/api/clients/${encodeURIComponent(clientId)}`, "PATCH", patch);
  return (d.client as Client) ?? ({ id: clientId, ...patch } as Client);
}

export async function patchSite(siteId: string, patch: Partial<Pick<Site, "name" | "policy">>): Promise<Site> {
  const d = await jsend(`/api/sites/${encodeURIComponent(siteId)}`, "PATCH", patch);
  return (d.site as Site) ?? ({ id: siteId, ...patch } as Site);
}

export async function deleteClient(clientId: string): Promise<void> {
  await jsend(`/api/clients/${encodeURIComponent(clientId)}`, "DELETE");
}

export async function deleteSite(siteId: string): Promise<void> {
  await jsend(`/api/sites/${encodeURIComponent(siteId)}`, "DELETE");
}

// ── Grants RBAC ──────────────────────────────────────────────────────────

export async function fetchGrants(): Promise<Grant[]> {
  const d = await jget<{ grants: Grant[] }>("/api/grants");
  return d.grants || [];
}

export async function createGrant(input: {
  userId: string;
  scopeType: string;
  scopeId: string;
  role: string;
  expiresAt?: string | null;
}): Promise<Grant> {
  const d = await jsend("/api/grants", "POST", input);
  return (d.grant as Grant) ?? ({ id: "", grantedAt: "", ...input } as Grant);
}

export async function deleteGrant(grantId: string): Promise<void> {
  await jsend(`/api/grants/${encodeURIComponent(grantId)}`, "DELETE");
}

// ── Opérateurs ───────────────────────────────────────────────────────────

export async function fetchOperators(): Promise<Operator[]> {
  const d = await jget<{ operators: Operator[] }>("/api/operators");
  return d.operators || [];
}

// ── Partages inter-tenants (clients uniquement) ──────────────────────────

export async function fetchShares(clientId: string): Promise<Share[]> {
  const d = await jget<{ shares: Share[] }>(`/api/clients/${encodeURIComponent(clientId)}/shares`);
  return d.shares || [];
}

export async function createShare(input: { clientId: string; tenantId: string; accessLevel: string }): Promise<Share> {
  const d = await jsend(`/api/clients/${encodeURIComponent(input.clientId)}/shares`, "POST", {
    tenantId: input.tenantId,
    accessLevel: input.accessLevel,
  });
  return (d.share as Share) ?? ({ grantedAt: "", ...input } as Share);
}

export async function deleteShare(clientId: string, tenantId: string): Promise<void> {
  await jsend(`/api/clients/${encodeURIComponent(clientId)}/shares/${encodeURIComponent(tenantId)}`, "DELETE");
}
